// Mirrors apps/api/src/lib/webhook-mapping.ts lookupPath (path walk, label
// selector `name[key=value]`, comma-joined choice lists) — keep in sync.
// Both are tested against tests/fixtures/webhook-path-cases.json.

/**
 * Walk a dotted path into an arbitrary JSON value. Returns undefined if any
 * segment is missing or hits a non-object/array intermediate. Coerces the
 * final value to string (we don't ship arbitrary JSON downstream). A final
 * array is undefined unless `joinLists` is set (HQ rule only): then a list of
 * scalars / `{value}` choice objects (Onepage multi choice) becomes one comma
 * text ("Content, Kommunikation").
 */
export function resolvePath(
  payload: unknown,
  path: string,
  opts?: { joinLists?: boolean },
): string | undefined {
  if (!path) return undefined;
  const cur = walkSegments(payload, splitPath(path));
  if (cur === null || cur === undefined) return undefined;
  if (Array.isArray(cur)) {
    if (!opts?.joinLists) return undefined;
    const parts: string[] = [];
    for (const el of cur) {
      const t = scalarText(isPlainObject(el) ? el["value"] : el);
      if (t === undefined) return undefined;
      if (t) parts.push(t);
    }
    return parts.length > 0 ? parts.join(", ") : undefined;
  }
  return scalarText(cur) || undefined;
}

type PathSeg = string | { listKey: string; listValue: string };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Trimmed text of a string/number/boolean, else undefined ("" for blank). */
function scalarText(v: unknown): string | undefined {
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return undefined;
}

/**
 * `path.split(".")` plus the list selector `name[key=value]`, which becomes
 * the segment `name` followed by a selector. A `[` that doesn't form a valid
 * selector stays literal, so keys containing brackets keep working.
 */
function splitPath(path: string): PathSeg[] {
  const segs: PathSeg[] = [];
  let buf = "";
  let afterSelector = false;
  // The first closing `]` (followed by `.` or the end) after the current `[` is
  // the same for every later `[` up to it, so remember it: one scan, not one
  // per bracket (a path of 50k `[` must not cost 50k × 50k).
  let closeAt: number | undefined;
  let eqAt: number | undefined;
  for (let i = 0; i < path.length; i++) {
    const ch = path[i]!;
    if (ch === "[") {
      if (closeAt === undefined || (closeAt !== -1 && closeAt <= i)) {
        closeAt = -1;
        for (let j = i + 1; j < path.length; j++) {
          if (path[j] === "]" && (j + 1 === path.length || path[j + 1] === ".")) {
            closeAt = j;
            break;
          }
        }
      }
      const end = closeAt;
      // same trick for the first `=` (the selector needs one, after >= 1 char)
      if (eqAt === undefined || (eqAt !== -1 && eqAt <= i)) eqAt = path.indexOf("=", i + 1);
      if (end > 0 && eqAt > i + 1 && eqAt < end) {
        if (buf) segs.push(buf);
        buf = "";
        segs.push({ listKey: path.slice(i + 1, eqAt), listValue: path.slice(eqAt + 1, end).trim() });
        i = end;
        afterSelector = true;
        continue;
      }
    }
    if (ch === ".") {
      if (!afterSelector) segs.push(buf);
      buf = "";
      afterSelector = false;
      continue;
    }
    buf += ch;
    afterSelector = false;
  }
  if (!afterSelector) segs.push(buf);
  return segs;
}

/**
 * Segment walk that tolerates object keys containing literal dots (e.g.
 * Perspective's fieldsMap: `"Step 1. Eintragung … → Email"`). At each object
 * level, first try the single segment, then progressively longer re-joins
 * of the remaining segments against the actual keys.
 */
function walkSegments(cur: unknown, segs: PathSeg[]): unknown {
  if (segs.length === 0) return cur;
  if (cur === null || cur === undefined) return undefined;
  const head = segs[0]!;
  if (typeof head !== "string") {
    if (!Array.isArray(cur)) return undefined;
    const el = cur.find(
      (e) => isPlainObject(e) && scalarText(e[head.listKey])?.toLowerCase() === head.listValue.toLowerCase(),
    );
    return walkSegments(el, segs.slice(1));
  }
  if (Array.isArray(cur)) {
    const idx = parseInt(head, 10);
    if (!Number.isFinite(idx)) return undefined;
    return walkSegments(cur[idx], segs.slice(1));
  }
  if (typeof cur !== "object") return undefined;
  const obj = cur as Record<string, unknown>;
  for (let n = 1; n <= segs.length; n++) {
    const part = segs[n - 1]!;
    if (typeof part !== "string") break;
    const key = (segs.slice(0, n) as string[]).join(".");
    if (key in obj) {
      const v = walkSegments(obj[key], segs.slice(n));
      if (v !== undefined) return v;
    }
  }
  return undefined;
}
